export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  CAD = 'CAD',
  AUD = 'AUD',
  JPY = 'JPY',
}

export type ConversionCurrency =
  | Currency.USD
  | Currency.EUR
  | Currency.GBP
  | Currency.CAD;

export const CONVERSION_RATES: Record<
  ConversionCurrency,
  Record<ConversionCurrency, number>
> = {
  [Currency.GBP]: {
    [Currency.EUR]: 1.18,
    [Currency.USD]: 1.33,
    [Currency.CAD]: 1.69,
    [Currency.GBP]: 1,
  },
  [Currency.EUR]: {
    [Currency.GBP]: 1 / 1.18,
    [Currency.USD]: 1.13,
    [Currency.CAD]: 1.43,
    [Currency.EUR]: 1,
  },
  [Currency.USD]: {
    [Currency.GBP]: 1 / 1.33,
    [Currency.EUR]: 1 / 1.13,
    [Currency.CAD]: 1.27,
    [Currency.USD]: 1,
  },
  [Currency.CAD]: {
    [Currency.USD]: 1 / 1.27,
    [Currency.EUR]: 1 / 1.43,
    [Currency.GBP]: 1 / 1.69,
    [Currency.CAD]: 1,
  },
};

export const roundCurrencyValue = (value: number, currency: string): number => {
  const decimalPlaces = currency === Currency.JPY ? 0 : 2;

  const multiplier = 10 ** decimalPlaces;
  return Math.round(value * multiplier) / multiplier;
};

export const convertCurrency = (
  amount: number,
  fromCurrency: string | null | undefined,
  toCurrency: string | null | undefined,
): number => {
  const from = (fromCurrency as ConversionCurrency) || Currency.USD;
  const to = (toCurrency as ConversionCurrency) || Currency.USD;

  if (from === to) {
    return amount;
  }

  if (CONVERSION_RATES[from] && CONVERSION_RATES[from][to]) {
    const rate = CONVERSION_RATES[from][to];
    const convertedAmount = amount * rate;
    const roundedAmount = roundCurrencyValue(convertedAmount, to);
    return roundedAmount;
  }

  return amount;
};

export const getCurrencySymbol = (
  currencyCode: string = Currency.USD,
): string => {
  switch (currencyCode) {
    case Currency.GBP: {
      return '£';
    }
    case Currency.EUR: {
      return '€';
    }
    case Currency.USD: {
      return '$';
    }
    default: {
      return '$';
    }
  }
};

export const formatCurrency = (
  amount: number,
  currencyCode: string = Currency.USD,
  locale = 'en-US',
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};
